// The page data a block can be judged against — what the visibility rules and
// the "Use variables" pickers offer.
//
// The site addresses this data by path (`product.category_id`), and that path
// is what the block stores and what the site evaluates, so the paths here are
// unchanged. What this module adds is what the editor *shows*: a name for the
// field, and — where the value behind it is an id — which list that id can be
// picked from. Without it a rule reads "product.c… != 47", which says nothing
// to the person setting up the page.
//
// The names come from a live product (`.port/probe/fixtures/live/product.json`
// in the theme), not from guesswork: there `id` is 10550 while the same
// product's images carry `variant_id: 10550, product_id: 1022` and the URL is
// /product/1022-… — so `product.id` is the variant and `product.product_id`
// the product. `category_id` is "47" on that product, which is the id the
// category list returns.

// value.kind tells the editor what can fill the right-hand side of a rule:
//   category        — pick from the shop's category tree
//   number / text   — typed in, no list to pick from
const FIELDS = {
  "category.id": {
    label: "Category",
    group: "Category page",
    value: { kind: "category" },
  },
  "product.category_id": {
    label: "Category",
    group: "This product",
    value: { kind: "category" },
  },
  "product.id": { label: "Variant ID", group: "Identifiers" },
  "product.product_id": { label: "Product ID", group: "Identifiers" },
  "product.serial": { label: "Serial number", group: "Identifiers" },
  "product.sku": { label: "SKU", group: "Identifiers" },
  "product.ean": { label: "EAN", group: "Identifiers" },
  "product.upc": { label: "UPC", group: "Identifiers" },
  "product.mpn": { label: "MPN", group: "Identifiers" },
  "product.stock": {
    label: "Stock",
    group: "Price & stock",
    value: { kind: "number" },
  },
  "product.avg_monthly_orders": {
    label: "Average monthly orders",
    group: "Price & stock",
    value: { kind: "number" },
  },
  "product.preorder": {
    label: "Preorder",
    group: "Price & stock",
    value: { kind: "number", hint: "1 = preorder, 0 = not" },
  },
  "product.price.amount": {
    label: "Price",
    group: "Price & stock",
    value: { kind: "number" },
  },
  "product.price.currency": {
    label: "Price currency",
    group: "Price & stock",
    value: { kind: "text", hint: "Currency code, e.g. HUF" },
  },
  "product.discount": {
    label: "Discount",
    group: "Price & stock",
    value: { kind: "number" },
  },
};

// product.schemas.<key>.id — the id of the value this product carries for that
// attribute. The attribute names are the field names made readable; the ids
// behind them are not listed anywhere the editor can reach, so those stay
// typed in.
const SCHEMA_LABELS = {
  brand: "Brand",
  author: "Author",
  color: "Colour",
  manufacturer: "Manufacturer",
  material: "Material",
  model: "Model",
  release_date: "Release date",
  size: "Size",
  pattern: "Pattern",
  is_adult: "Adult only",
  bundle: "Bundle",
  energy_efficiency_class: "Energy efficiency class",
  age_group: "Age group",
  gender: "Gender",
  size_system: "Size system",
  weight: "Weight",
  box_size: "Box size",
  line_name: "Line name",
};

// Which paths each kind of page can be asked about. Page types as the API
// numbers them: 1 = category list, 2 = product page, 3 = bundle page.
export const VARIABLE_PATHS = {
  1: ["category.id"],
  2: [
    "product.category_id",
    "product.serial",
    "product.sku",
    "product.ean",
    "product.upc",
    "product.mpn",
    "product.id",
    "product.product_id",
    "product.stock",
    "product.avg_monthly_orders",
    "product.preorder",
    "product.price.amount",
    "product.price.currency",
    "product.discount",
    ...Object.keys(SCHEMA_LABELS).map((key) => `product.schemas.${key}.id`),
  ],
  3: ["product.category_id"],
};

export const OPERATORS = [
  { value: "==", label: "is" },
  { value: "!=", label: "is not" },
  { value: "<", label: "is less than" },
  { value: ">", label: "is more than" },
  { value: "<=", label: "is at most" },
  { value: ">=", label: "is at least" },
  { value: "in", label: "is one of" },
  { value: "not in", label: "is not one of" },
];

export function operatorLabel(operator) {
  return OPERATORS.find((o) => o.value === operator)?.label ?? operator;
}

// A path nobody wrote a name for still gets something readable: the last
// meaningful segment, underscores out, capitalised.
function humanise(path) {
  const parts = String(path ?? "")
    .split(".")
    .filter((part) => part !== "id" && part !== "");
  const last = parts[parts.length - 1] ?? String(path ?? "");
  const words = last.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// One option for the pickers: `value` is what gets stored (unchanged), the
// rest is only shown.
export function describeVariable(path) {
  const known = FIELDS[path];
  if (known) {
    return {
      value: path,
      label: known.label,
      hint: path,
      group: known.group,
      valueKind: known.value?.kind ?? "text",
      valueHint: known.value?.hint ?? null,
    };
  }
  const schema = /^product\.schemas\.([a-z_]+)\.id$/.exec(path ?? "");
  if (schema) {
    return {
      value: path,
      label: SCHEMA_LABELS[schema[1]] ?? humanise(schema[1]),
      hint: path,
      group: "Product attributes",
      valueKind: "text",
      valueHint: "The id of the value, as the product carries it",
    };
  }
  const characteristic = /^product\.characteristics\.(\d+)\.id$/.exec(
    path ?? ""
  );
  if (characteristic) {
    return {
      value: path,
      label: `Characteristic #${characteristic[1]}`,
      hint: path,
      group: "Characteristics",
      valueKind: "characteristic",
      characteristicId: Number(characteristic[1]),
      valueHint: null,
    };
  }
  return {
    value: path,
    label: humanise(path),
    hint: path,
    group: "Other",
    valueKind: "text",
    valueHint: null,
  };
}

const GROUP_ORDER = [
  "This product",
  "Category page",
  "Price & stock",
  "Identifiers",
  "Product attributes",
  "Characteristics",
  "Other",
];

// The list a page type offers, named and grouped. `characteristics` (as the
// filter endpoint returns them) only renames the characteristic paths that are
// already in the list — it never adds new ones, because whether the site can
// evaluate a characteristic in a visibility rule is the backend's answer to
// give, not ours.
export function pageVariables(pageType, characteristics = []) {
  const paths = VARIABLE_PATHS[pageType] ?? [];
  const options = paths.map((path) => describeVariable(path));
  return sortByGroup(nameCharacteristics(options, characteristics));
}

// Turns "Characteristic #38" into the name the shop gave it ("Colour family").
export function nameCharacteristics(options, characteristics = []) {
  if (!characteristics?.length) {
    return options;
  }
  return options.map((option) => {
    if (option.characteristicId === undefined) {
      return option;
    }
    const match = characteristics.find(
      (c) => String(c.id) === String(option.characteristicId)
    );
    return match?.name ? { ...option, label: match.name } : option;
  });
}

export function sortByGroup(options) {
  return [...options].sort((a, b) => {
    const groupDiff =
      GROUP_ORDER.indexOf(a.group ?? "Other") -
      GROUP_ORDER.indexOf(b.group ?? "Other");
    return groupDiff !== 0 ? groupDiff : a.label.localeCompare(b.label);
  });
}

// The ready-made product lists a page can hand a block ("similar_products").
// Same problem as the variables: the key is the site's word for it, not a
// name anyone recognises in a dropdown.
const DYNAMIC_LABELS = {
  products: "The products listed on this page",
  similar_products: "Similar products",
  recommended_products: "Recommended for you",
  bundles_with_product:
    "Bundles that contain this product",
  co_bundle_products:
    "Other products that are also in a bundle with this product",
  other_bundles: "Other bundles with the same product",
};

const DYNAMIC_BLOCKS = {
  1: ["products"], //list
  2: [
    "similar_products",
    "recommended_products",
    "bundles_with_product",
    "co_bundle_products",
  ], //page (product)
  3: ["similar_products", "recommended_products", "other_bundles"], //bundle
};

export function dynamicBlocks(pageType) {
  return (DYNAMIC_BLOCKS[pageType] ?? []).map((key) => ({
    value: key,
    label: DYNAMIC_LABELS[key] ?? key,
    hint: key,
  }));
}
